/* import __COLOCATED_TEMPLATE__ from './suggestion-card.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';
import type AppService from 'embercom/services/app-service';
import { approveTaskSuggestion, rejectTaskSuggestion } from './task-suggestions-data-resources';
import { tracked } from '@glimmer/tracking';
import type ReportingChartService from 'embercom/services/reporting-chart-service';

interface Signature {
  Args: {
    suggestion: TaskSuggestion;
    onShowConversationExplorer: (
      conversationIds: string[],
      actionName: string,
      actionSummary: string,
      conversationCount: number,
    ) => void;
  };
}

export default class SuggestionCard extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;
  @service declare reportingChartService: ReportingChartService;

  @tracked isApproving = false;
  @tracked isRejecting = false;

  get isSyntheticData() {
    return this.reportingChartService.useSyntheticData;
  }

  @action
  onCardInserted(element: HTMLElement) {
    // Scroll this suggestion into view if it should auto-open
    if (this.shouldAutoOpenApiDocs) {
      // Use requestAnimationFrame to ensure the element is fully rendered
      requestAnimationFrame(() => {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    }
  }

  @action
  handleViewConversations() {
    this.args.onShowConversationExplorer(
      this.args.suggestion.sampledConversationIds,
      this.args.suggestion.actionName,
      this.args.suggestion.actionSummary,
      this.args.suggestion.conversationCount,
    );
  }

  @action
  async handleApprove() {
    if (this.isApproving || this.isSyntheticData) {
      return;
    }

    this.isApproving = true;
    try {
      await approveTaskSuggestion(this.store, this.appService.app.id, this.args.suggestion.id);

      // Show success notification
      this.notificationsService.notifyConfirmation(
        this.intl.t('automation.analyze.task-suggestions.suggestion-card.approved-successfully', {
          actionName: this.args.suggestion.actionName,
        }),
      );
    } catch (error) {
      console.error('Failed to approve task suggestion:', error);
      this.notificationsService.notifyError(
        this.intl.t('automation.analyze.task-suggestions.suggestion-card.approve-failed'),
      );
    } finally {
      this.isApproving = false;
    }
  }

  @action
  async handleReject() {
    if (this.isRejecting || this.isSyntheticData) {
      return;
    }

    this.isRejecting = true;
    try {
      await rejectTaskSuggestion(this.store, this.appService.app.id, this.args.suggestion.id);

      this.notificationsService.notifyConfirmation(
        this.intl.t('automation.analyze.task-suggestions.suggestion-card.rejected-successfully', {
          actionName: this.args.suggestion.actionName,
        }),
      );
    } catch (error) {
      console.error('Failed to reject task suggestion:', error);
      this.notificationsService.notifyError(
        this.intl.t('automation.analyze.task-suggestions.suggestion-card.reject-failed'),
      );
    } finally {
      this.isRejecting = false;
    }
  }

  get topicsString() {
    return this.args.suggestion.topicNameList.join(', ') || '';
  }

  get shouldAutoOpenApiDocs() {
    let queryParams = this.router.currentRoute?.queryParams;
    let suggestionId = queryParams?.suggestionId;
    let showApiDocs = queryParams?.showApiDocs;

    return suggestionId === this.args.suggestion.id && showApiDocs === 'true';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TaskSuggestions::SuggestionCard': typeof SuggestionCard;
    'reporting/automation/fin-ai-agent/analyze/task-suggestions/suggestion-card': typeof SuggestionCard;
  }
}
